import type {
  AlgorithmEntry,
  AlgorithmModelColumn,
  BatchRequest,
  ErrorBase,
  FileSpec,
  FilterSpec,
  MissingData,
  PlotMeasure,
  ReaderSpec,
} from '../types/compute';
import type { FileColumn } from './fileMetadata';
import {
  dedupeSpatialSolids,
  numericCategoricalToContinuous,
  periodOrFilterId,
} from './filterSpec';
import { collapseTonnageByCategory, expandTonnageByCategory } from './tonnageMode';
import { collapseErrorCutoff, expandErrorCutoff } from './errorCutoff';
// algorithmInstance imports only TYPES from this module, so this is not a
// runtime cycle — the type imports are erased.
import { uniqueInstanceIds } from './algorithmInstance';
import {
  ALGORITHM_OVERLAY,
  currentFigureTitle,
  errorUsesGlobalDensity,
} from '../config/catalogOverlay';

/**
 * UI-side draft of a reconciliation config. The editor binds to this
 * camelCase shape; `buildBatchRequest` lowers it to the snake_case
 * `BatchRequest` the worker + C++ wrapper consume.
 */
export interface ReaderInput {
  id: string;
  /** storage-hub file id — becomes both the files[] entry and the reader path. */
  fileId: string;
  delimiter: string;
  hasHeader: boolean;
  coordinatesIndexes: [number, number, number];
  delta: [number, number, number];
  min?: [number, number, number];
  max?: [number, number, number];
  // ---- UI-only (never emitted into the BatchRequest / wire contract) ----
  /** Display name from storage-hub; shown instead of the raw file id. */
  fileName?: string;
  /** Data columns from storage-hub metadata, for name-based model column pickers. */
  availableColumns?: FileColumn[];
  /** Every named column (incl. coordinates), for name-based X/Y/Z pickers. */
  allColumns?: FileColumn[];
}

export interface AlgorithmModelInput {
  readerId: string;
  columns: AlgorithmModelColumn[];
  /** This model's filter cards (leaf FilterSpecs). Lowered to the flat
   *  filters[] + model_filters wire shape at build time; ids are UI-managed
   *  and never surfaced to the user. Each card reads THIS model's columns. */
  filters?: FilterSpec[];
}

export interface AlgorithmInput {
  name: string;
  models: AlgorithmModelInput[];
  parameters: Record<string, unknown>;
  criterioSeleccion?: string;
  /** Which measures the tonnage charts plot (2–3); presentation-only. */
  plotMeasures?: PlotMeasure[];
  /**
   * This configured chart's stable identity (see lib/algorithmInstance). Minted
   * when the chart is created and carried from then on — deleting a sibling
   * never changes it, which is what keeps an executed chart's edit/duplicate/
   * delete working without a re-run.
   *
   * Optional on the type so the many fixtures that build an algorithm literal
   * don't all have to invent one. In the app it is always set: `batchRequestToDraft`
   * materialises it on load and the store mints it on add/duplicate, so every
   * draft the UI touches carries ids.
   */
  instanceId?: string;
  /** Session-only stash of each tonnage mode's parameters (keyed by
   *  TonnageUiMode), so switching modes and back restores what the target
   *  schema can't carry (US 2079). Never emitted: buildBatchRequest builds
   *  each wire entry explicitly. */
  modeStash?: Record<string, Record<string, unknown>>;
  /** Which error metrics the `error` chart draws; presentation-only. */
  errorBases?: ErrorBase[];
  /** The `error` chart's cut-off grade. Draft-only: it lowers to a per-model
   *  filter and is lifted back from it on load (lib/errorCutoff, SPEC/2423). */
  errorCutoff?: number;
  /** The `error` chart's tolerance, in percent; presentation-only (SPEC/2423). */
  errorTolerance?: number;
  /** This configured chart's user-facing name (see `AlgorithmEntry.figure_title`
   *  / SPEC/2240). Optional on the type for the same reason `instanceId` is —
   *  every draft the UI touches carries one, materialised by
   *  `batchRequestToDraft` and minted on add/duplicate. */
  figureTitle?: string;
}

export interface ReconciliationDraft {
  readers: ReaderInput[];
  algorithms: AlgorithmInput[];
}

/**
 * Emit a column's missing-data policy only when it has a real effect: a
 * Delete/SetTo with at least one anomalous value. An "Ok" or value-less policy
 * is a no-op — and, crucially, the C++ wrapper reads `anomalous_value`
 * unconditionally (`json.at`), so emitting a policy without it fails execution
 * with `key 'anomalous_value' not found`. Dropping the incomplete policy keeps
 * the payload valid; the editor separately blocks running until the user fills
 * the sentinels (algorithmValidity).
 */
function normalizeColumn(c: AlgorithmModelColumn): AlgorithmModelColumn {
  const md = c.missing_data;
  if (!md || md.policy === 'Ok' || !md.anomalous_value) {
    return { index: c.index, name: c.name };
  }
  const clean: MissingData = { policy: md.policy, anomalous_value: md.anomalous_value };
  if (md.policy === 'SetTo') clean.replacement_value = md.replacement_value ?? '';
  return { index: c.index, name: c.name, missing_data: clean };
}

/** Collect a spatial filter's solid file ids, if any. */
function solidFileIds(filters: FilterSpec[] | undefined): string[] {
  if (!filters) return [];
  const ids: string[] = [];
  for (const f of filters) {
    if (f.type === 'spatial') ids.push(...f.solids);
  }
  return ids;
}

// Filters are edited per-model in the UI, but the wire contract (SPEC/1489) is a
// flat filters[] pool + a fixed 2-slot model_filters. These helpers translate
// between them. A model's `combined` filter is its selector: its expression
// references that model's own filter ids (e.g. "f1 AND NOT f2"), and
// model_filters points at it. With a single filter and no combined the slot
// points straight at that filter; a model with ≥2 filters and no combined is an
// incomplete state (no slot emitted) that the editor blocks before save/execute.

/** Filter ids from `pool` that appear as whole tokens in a combined expression
 *  (e.g. "a AND NOT b" references a, b). Ids are word-char only (enforced by the
 *  FilterEditor), so splitting on non-word chars isolates the operands cleanly. */
function referencedIds(expression: string, pool: FilterSpec[]): Set<string> {
  const tokens = new Set(expression.split(/[^A-Za-z0-9_]+/).filter(Boolean));
  return new Set(pool.filter((f) => tokens.has(f.id)).map((f) => f.id));
}

function lowerModelFilters(models: AlgorithmModelInput[]): {
  filters: FilterSpec[];
  modelFilters: (string | null)[];
} {
  const filters: FilterSpec[] = [];
  const modelFilters: (string | null)[] = [null, null];
  models.slice(0, 2).forEach((m, i) => {
    const cards = m.filters ?? [];
    if (cards.length === 0) return;
    // Numeric-looking category values must run as continuous [n, n] — the C++
    // categorical filter bad-any_casts double cells (see filterSpec).
    filters.push(...cards.map(numericCategoricalToContinuous));
    // The periods-of-interest OR-group (US 2203) is its own `combined`, nested
    // inside the model's top-level AND rather than being the selector itself —
    // unless it's the ONLY combined present (no other filter on this model),
    // in which case it IS the selector.
    const orId = periodOrFilterId(i);
    const combined =
      cards.find((c) => c.type === 'combined' && c.id !== orId) ??
      cards.find((c) => c.type === 'combined' && c.id === orId);
    if (combined) modelFilters[i] = combined.id;
    else if (cards.length === 1) modelFilters[i] = cards[0].id;
    // else ≥2 leaves without a combined selector: leave the slot null.
  });
  // Both models often reference the same solid (one geometry gating both). Merge
  // spatial cards with identical solids so the wrapper builds the volume once.
  return dedupeSpatialSolids(filters, modelFilters);
}

export function liftModelFilters(a: AlgorithmEntry, m: number): FilterSpec[] {
  const fid = a.model_filters?.[m];
  if (!fid) return [];
  const pool = a.filters ?? [];
  const entry = pool.find((f) => f.id === fid);
  if (!entry) return [];
  // A leaf slot → that one card. A combined selector → the combined plus the
  // leaves its expression references, kept in pool order so the round-trip is
  // stable (a legacy __and_m* synth simply loads as a visible combined card).
  // A combined can itself reference another combined — the periods-of-interest
  // OR-group (US 2203) sits inside the model's top-level AND this way — so the
  // reference walk is transitive, following the chain until nothing new turns up.
  if (entry.type !== 'combined') return [entry];
  const included = new Set([fid]);
  let frontier: FilterSpec[] = [entry];
  while (frontier.length > 0) {
    const next: FilterSpec[] = [];
    for (const f of frontier) {
      if (f.type !== 'combined') continue;
      for (const id of referencedIds(f.expression, pool)) {
        if (included.has(id)) continue;
        included.add(id);
        const ref = pool.find((p) => p.id === id);
        if (ref) next.push(ref);
      }
    }
    frontier = next;
  }
  return pool.filter((f) => included.has(f.id));
}

/**
 * Lower a UI draft into the canonical `BatchRequest`. Files are
 * deduplicated (first-seen order) and gathered from BOTH reader file ids and
 * spatial-filter solid ids — the worker's resolveFiles walks the whole payload
 * and substitutes any string matching a files[] id, so every referenced id
 * must be listed. Each reader's path is set to its file id for that pass.
 */
export function buildBatchRequest(draft: ReconciliationDraft): BatchRequest {
  const files: FileSpec[] = [];
  const seen = new Set<string>();
  const addFile = (id: string) => {
    if (!seen.has(id)) {
      seen.add(id);
      files.push({ id });
    }
  };
  for (const r of draft.readers) addFile(r.fileId);
  for (const a of draft.algorithms)
    for (const m of a.models) for (const id of solidFileIds(m.filters)) addFile(id);

  const readers: ReaderSpec[] = draft.readers.map((r) => {
    const reader: ReaderSpec = {
      id: r.id,
      path: r.fileId,
      delimiter: r.delimiter,
      has_header: r.hasHeader,
      coordinates_indexes: r.coordinatesIndexes,
      delta: r.delta,
    };
    if (r.min) reader.min = r.min;
    if (r.max) reader.max = r.max;
    return reader;
  });

  // Scenario 3: a "por categoría y ley de corte" tonnage entry fans out into one
  // filtered by_cutoff call per category before lowering (see expandTonnageByCategory).
  // The `error` cut-off lowers to a per-model filter the same way (lib/errorCutoff).
  const algorithms: AlgorithmEntry[] = draft.algorithms
    .flatMap(expandTonnageByCategory)
    .map(expandErrorCutoff)
    .map((a) => {
      // `volume` is the block volume — derive it from the model[0] reader's grid
      // (Δx·Δy·Δz) rather than ask for it. Only for algorithms that take it.
      let parameters = a.parameters;
      if ('volume' in parameters) {
        const reader = readers.find((r) => r.id === a.models[0]?.readerId);
        if (reader) {
          const [dx, dy, dz] = reader.delta;
          parameters = { ...parameters, volume: dx * dy * dz };
        }
      }
      // HACK(lib-2165): the accumulative mode's grade series is the quotient of two
      // accumulated ERRORS, so the chart prints a number that is neither an error
      // nor a grade (see catalogOverlay's is_accumulative_error). The form no longer
      // offers it; forcing it here is what covers a reconciliation SAVED with `true`
      // — nothing normalizes a persisted config on load, and the user can no longer
      // see the checkbox to untick it. Forced at lowering rather than in the store so
      // instanceStaleness, which compares two LOWERED entries, passes both sides
      // through the same rule and doesn't flag an old reconciliation as stale.
      // Keyed on the algorithm, not on the key being present: `AlgoritmoError.h`
      // reads it with Get<bool>() and no Has guard, so an absent key throws in C++
      // — reachable from a migrated or hand-edited config now that the form no
      // longer renders the checkbox.
      //
      // `use_global_density` is lowered here for the same reason: the Lib reads it
      // with Get<bool>() and no Has guard (SPEC/2597), but a config saved before
      // has no flag — it picked the mode by the presence of `global_density`.
      if (a.name === 'error') {
        parameters = {
          ...parameters,
          is_accumulative_error: false,
          use_global_density: errorUsesGlobalDensity(parameters),
        };
      }
      const entry: AlgorithmEntry = {
        name: a.name,
        models: a.models.map((m) => ({
          reader_id: m.readerId,
          columns: m.columns.map(normalizeColumn),
        })),
        parameters,
      };
      const { filters, modelFilters } = lowerModelFilters(a.models);
      if (filters.length > 0) entry.filters = filters;
      if (modelFilters.some((x) => x !== null)) entry.model_filters = modelFilters;
      if (a.criterioSeleccion) entry.criterio_seleccion = a.criterioSeleccion;
      if (a.plotMeasures && a.plotMeasures.length > 0) entry.plot_measures = a.plotMeasures;
      if (a.errorBases && a.errorBases.length > 0) entry.error_bases = a.errorBases;
      if (a.name === 'error' && a.errorTolerance !== undefined) {
        entry.error_tolerance = a.errorTolerance;
      }
      // The configured chart this entry came from. Written for every entry, so the
      // fan-out's N entries all carry their one parent chart's id — that is what
      // lets several result envelopes resolve back to a single draft algorithm.
      if (a.instanceId) entry.instance_id = a.instanceId;
      if (a.figureTitle) entry.figure_title = a.figureTitle;
      return entry;
    });

  return { files, readers, algorithms };
}

/**
 * Inverse of `buildBatchRequest`: lower a stored config (a reconciliation's
 * `config` or an instance's `configSnapshot`) back into an editable draft.
 * Used to edit an existing reconciliation and to seed "copy this instance".
 * `files[]` is dropped — `buildBatchRequest` regenerates it from readers +
 * spatial-filter solids.
 */
export function batchRequestToDraft(config: BatchRequest): ReconciliationDraft {
  const readers: ReaderInput[] = (config.readers ?? []).map((r) => {
    const input: ReaderInput = {
      id: r.id,
      fileId: r.path,
      delimiter: r.delimiter,
      hasHeader: r.has_header,
      coordinatesIndexes: r.coordinates_indexes,
      delta: r.delta,
    };
    if (r.min) input.min = r.min;
    if (r.max) input.max = r.max;
    return input;
  });

  // Every saved figure name, so a renamed default never collides with one (Bug 2732).
  const savedTitles = (config.algorithms ?? []).flatMap((a) =>
    a.figure_title ? [a.figure_title] : []
  );
  const algorithms: AlgorithmInput[] = (config.algorithms ?? []).map((a) => {
    const input: AlgorithmInput = {
      name: a.name,
      models: a.models.map((m, mi) => {
        const model: AlgorithmModelInput = { readerId: m.reader_id, columns: m.columns };
        const cards = liftModelFilters(a, mi);
        if (cards.length > 0) model.filters = cards;
        return model;
      }),
      parameters: a.parameters,
    };
    if (a.criterio_seleccion) input.criterioSeleccion = a.criterio_seleccion;
    if (a.plot_measures && a.plot_measures.length > 0) input.plotMeasures = a.plot_measures;
    if (a.instance_id) input.instanceId = a.instance_id;
    if (a.error_bases && a.error_bases.length > 0) input.errorBases = a.error_bases;
    if (a.error_tolerance !== undefined) input.errorTolerance = a.error_tolerance;
    if (a.figure_title) input.figureTitle = currentFigureTitle(a.name, a.figure_title, savedTitles);
    return collapseErrorCutoff(input);
  });

  // Every draft the UI touches carries identities, so nothing downstream needs a
  // positional fallback. A config saved before minting gets its implied ids
  // written down here — the same strings that scheme derived, which is why the
  // reconciliation's existing snapshots and selections keep resolving. Entries
  // sharing an id (a lowered fan-out, loaded as separate charts) are split.
  // Fold a lowered category fan-out back into the one card it came from BEFORE
  // materialising ids: `uniqueInstanceIds` would otherwise see its N entries sharing
  // an id and split them into N independent charts (see collapseTonnageByCategory).
  const identified = uniqueInstanceIds(collapseTonnageByCategory(algorithms));

  // Same materialisation for the figure name (SPEC/2240): a config saved before
  // this feature has no figure_title, so write down exactly the label the UI
  // already showed for it — `${label} #${occurrence}`, 1-based over instances of
  // that name IN THIS ORDER — so opening an existing reconciliation doesn't
  // change the name it's known by. Occurrence walks every entry regardless of
  // whether it already carries a name, matching how ensureInstanceIds counts.
  const occurrenceByName = new Map<string, number>();
  const withFigureTitles = identified.map((a) => {
    const occurrence = (occurrenceByName.get(a.name) ?? 0) + 1;
    occurrenceByName.set(a.name, occurrence);
    if (a.figureTitle) return a;
    const label = ALGORITHM_OVERLAY[a.name]?.label ?? a.name;
    return { ...a, figureTitle: `${label} #${occurrence}` };
  });

  return { readers, algorithms: withFigureTitles };
}
